import React from 'react';
import styled from 'styled-components';
import { Box, Stack } from '@99designs/emu';
import { colors, typography } from '@99designs/emu/settings';
import { FormattedReactMessage } from '@99designs/frontend-components/i18n';
import { ViewProductDetails } from '@99designs/spa-common';
import { trackEvent } from '@99designs/tracking';
import { useOnboarder } from '../../../components/Onboarder/OnboarderContext';
import { Price } from '../../../components/Price';
import { svgBase64ToAscii } from '../../../utils/svgBase64ToAscii';
import { ProductCard } from '../../ProductCard';
import {
    StartBriefButton,
    StartContestBriefProvider,
    StartProjectBriefProvider,
} from '../../StartBrief';
import { ONBOARDER_NAME } from '../../eventTracking';
import { useLocalExchangeRate } from '../LocalExchangeRateContext';
import type { Product } from '../../Product';
import type { Data } from '../../config';

interface Props {
    product: Product;
}

const PriceConversionDisclaimer = styled.div`
    text-align: right;
    font-size: ${typography.typeScale.size2}px;
    color: ${colors.grey700};
`;

function getTitleForProduct(product: Product) {
    if (product.pricing.isFixed || product.deliverableType === 'website-design') {
        return product.name;
    }
    return __('Custom project');
}

export function RecommendationCard({ product }: Props) {
    const { data } = useOnboarder<Data>();
    const exchangeRate = useLocalExchangeRate();
    const localisedPricing = product.getLocalisedPricing(exchangeRate);
    const showPricingConversionDisclaimer =
        // eslint-disable-next-line eqeqeq
        localisedPricing.fromPrice.currency != 'USD' && localisedPricing.isFixed;
    const productSummaryProps = {
        title: getTitleForProduct(product),
        description: product.description,
        logo: product.logo ? svgBase64ToAscii(product.logo) : undefined,
        fromPrice: localisedPricing.fromPrice,
        showFixedPrice: localisedPricing.isFixed,
    };

    return (
        <div data-testid="recommendation-card">
            <ProductCard
                productSummary={productSummaryProps}
                features={product.features}
                rightColumn={
                    <Stack space="1.5bu">
                        {product.pricing.isFixed && (
                            <Box display="flex" justifyContent="center">
                                <ViewProductDetails
                                    features={product.features}
                                    featureColor={product.featureColor}
                                    additionalDetails={product.additionalDetails}
                                    productSummary={productSummaryProps}
                                    onClick={() => {
                                        trackEvent('Clicked Onboarder Element', {
                                            onboarder_name: ONBOARDER_NAME,
                                            element_name: `Product modal link for ${product.key}`,
                                        });
                                    }}
                                />
                            </Box>
                        )}
                        {product.workingMethod === 'contest' && (
                            <StartContestBriefProvider category={product.getCategory()} data={data}>
                                {({ loading, startBrief }) => (
                                    <StartBriefButton
                                        loading={loading}
                                        onClick={() => {
                                            startBrief();
                                        }}
                                    >
                                        {__('Start brief')}
                                    </StartBriefButton>
                                )}
                            </StartContestBriefProvider>
                        )}
                        {product.workingMethod === 'project' && (
                            <StartProjectBriefProvider data={product.getCreateBriefVariables(data)}>
                                {({ loading, startBrief }) => (
                                    <StartBriefButton
                                        loading={loading}
                                        onClick={() => {
                                            startBrief();
                                        }}
                                    >
                                        {product.pricing.isFixed
                                            ? __('Start brief')
                                            : __('Get quote')}
                                    </StartBriefButton>
                                )}
                            </StartProjectBriefProvider>
                        )}
                    </Stack>
                }
            />
            {showPricingConversionDisclaimer && (
                <Box paddingTop="1.5bu">
                    {/* eslint-disable-next-line jsx-a11y/aria-role */}
                    <PriceConversionDisclaimer role={'price-disclaimer'}>
                        <FormattedReactMessage
                            message={__('Price converted from <FromPrice>price_noop</FromPrice>')}
                            replacements={{
                                FromPrice: (
                                    <Price
                                        amountInCents={product.pricing.fromPrice.amountInCents}
                                        currency={product.pricing.fromPrice.currency}
                                    />
                                ),
                            }}
                        />
                    </PriceConversionDisclaimer>
                </Box>
            )}
        </div>
    );
}
